// Third party library imports
import { inject } from '@angular/core';
import {
    patchState,
    signalStore,
    withMethods,
    withState,
} from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';
import { DateTime } from 'luxon';

// Zopup library imports
import { StorageKey, StorageService } from '@zopup/zop-shared';
import {
    BizError,
    BooleanWebApiResponse,
    StatutoryApprovedCommand,
    StatutoryCreateCommand,
    StatutoryInfoCreateDto,
    StatutoryInfoDto,
    StatutoryInfoEditDto,
    StatutoryRejectCommand,
    StatutoryUpdateCommand,
    SubcriptionPackageChangeCommand,
    SubscriptionCreateCommand,
    SubscriptionDto,
    SubscriptionExtendTrailCommand,
    TenantCreateCommand,
    TenantDto,
    TenantDtoListWebApiPagedResponse,
    TenantDtoWebApiResponse,
    TenantEditDto,
    TenantPagedListQuery,
    TenantUpdateCommand,
} from '@zopup/zop-types';

// Internal project imports
import { TenantService } from './tenant.service';

export type TenantWrapper = {
    id: string;
    referralCode?: string;
    tenantName: string;
    businessName?: string;
    designation?: string;
    email?: string;
    mobileNumber?: string;
}

type TenantState = {
    tenants: TenantDto[];
    tenantsList: TenantWrapper[];
    subscribedPackages: SubscriptionDto[];
    statutories: StatutoryInfoDto[];
    selectedTenant: TenantDto;
    isLoading: boolean;
    errors: BizError[];
    totalCount: number;
    query: TenantPagedListQuery;
};

export const tenantInitialState: TenantState = {
    tenants: [],
    tenantsList: [],
    selectedTenant: {
        id: ''
    },
    subscribedPackages: [],
    statutories: [],
    isLoading: false,
    errors: [],
    query: {
        pageNumber: 1,
        pageSize: 10
    },
    totalCount: 0,
};

export const TenantStore = signalStore(
    { providedIn: 'root' },
    withState(tenantInitialState),
    withMethods((
        store,
        tenantService = inject(TenantService),
        storageService = inject( StorageService)
        ) => ({
        onSelectTenant: async(tenant: TenantDto)=> {
            patchState(store, { 
                selectedTenant: tenant,
                subscribedPackages: tenant?.subscriptions!,
                statutories: tenant?.statutories!
            });
        },
        updateQuery(query: TenantPagedListQuery): void {
            patchState(store, {
                query: query,
            });
        },
        updateOrder(order: 'asc' | 'desc'): void {
            patchState(store, (state) => ({
                query: { ...state.query, order },
            }));
        },

        // Get list of Tenants
        getPagedList: async () => {
            let tenantResponse!: TenantDtoListWebApiPagedResponse;
            try{
                patchState(store, { isLoading: true });
                let query = store.query();
                tenantResponse = await lastValueFrom(
                    tenantService.tenantGet(
                        query.city, 
                        query.pincode,
                        query.district,
                        query.state,
                        query.country,
                        query.packageId,
                        query.status,
                        query.fromTime,
                        query.toTime,
                        query.orderBy,
                        query.isOrderByDescending,
                        query.searchText,
                        query.pageNumber,
                        query.pageSize
                    )
                );
                if (tenantResponse.isSuccess) {
                    //Convert the Service Response to the format to be binded to the table
                    const tenantsList: TenantWrapper[] = [];
                        tenantResponse.result?.forEach((tenant: TenantDto)=>{
                        tenantsList.push({
                            id: tenant.id!,
                            referralCode: tenant.referralCode!,
                            tenantName: `${tenant.salutation}. ${tenant.firstName} ${tenant.lastName}`,
                            businessName: tenant.businessName!,
                            designation: tenant.designation!,
                            email: tenant?.email?.value,
                            mobileNumber: `${tenant?.mobileNumber?.countryCode}-${tenant?.mobileNumber?.value}`
                        });
                    });
                    
                    //Update the store
                    patchState(store, {
                        tenants: tenantResponse.result!,
                        tenantsList: tenantsList,
                        totalCount: tenantResponse?.totalCount,
                        isLoading: false,
                    });
                } else {
                    patchState(store, {
                        tenants: tenantResponse.result!,
                        totalCount: 0,
                        tenantsList: [],
                        errors: tenantResponse.errors!,
                        isLoading: false,
                    });
                }
            }catch (error) {
                console.error('Error in TenantStore.getPagedList(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
            }
            return tenantResponse;
        },

        //Get the selected tenant Details
        getById: async (tenantId?: string) => {

            try{
                if(tenantId){
                    patchState(store, { isLoading: true });
                    const selTenant = await lastValueFrom(tenantService.tenantIdGet(tenantId));
                    if (selTenant.isSuccess) {
                        // Sort the Subscriptions in descending order of startDate
                        const tenantSubscriptions = selTenant.result?.subscriptions;
                        if(tenantSubscriptions?.length! > 1){
                            tenantSubscriptions?.sort((a, b) => {
                                if (!a.startDate && !b.startDate) return 0;
                                if (!a.startDate) return 1;
                                if (!b.startDate) return -1;
                                return DateTime.fromISO(b.startDate.toString()).toMillis() 
                                        - DateTime.fromISO(a.startDate.toString()).toMillis();
                            });
                        }
                        // Sort the Remarks of Every Statutory Information
                        const tenantStatutories = selTenant.result?.statutories;
                        if(tenantStatutories?.length! > 1){
                            tenantStatutories?.forEach((statutory)=>{
                                const remarks = statutory?.remarks;
                                if(remarks?.length! > 1){
                                        remarks?.sort((a, b) => {
                                            if (!a.createdOn && !b.createdOn) return 0;
                                            if (!a.createdOn) return 1;
                                            if (!b.createdOn) return -1;
                                            return DateTime.fromISO(b.createdOn.toString()).toMillis() 
                                                - DateTime.fromISO(a.createdOn.toString()).toMillis();
                                    });
                                }
                                statutory.remarks = remarks;
                            });
                        }
                        
                        //Update the store
                        patchState(store, {
                            selectedTenant: selTenant.result,
                            subscribedPackages: tenantSubscriptions!,
                            statutories: tenantStatutories!,
                            isLoading: false,
                        });
                    } else {
                        patchState(store, {
                            errors: selTenant.errors,
                            isLoading: false,
                        });
                    }
                }
            }catch (error) {
                console.error('Error in TenantStore.getById(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
                throw error;
            }
        },

        // Create new Tenant
        add: async (
            tenantCommand: TenantCreateCommand,
        ): Promise<TenantDtoWebApiResponse> => {
            let tenantResponse!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                tenantResponse = await lastValueFrom(
                    tenantService.tenantPost(tenantCommand)
                );
                patchState(store, { isLoading: false });
            }catch (error) {
                console.error('Error in TenantStore.add(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
                tenantResponse = {
                    isSuccess: false,
                    message: 'Tenant Creation Failed',
                    errors: [],
                    result: undefined
                }
            }
            return tenantResponse;
        },

        // Update existing tenant
        update: async (
            selTenant: TenantEditDto
        ): Promise<TenantDtoWebApiResponse> => {

            let tenantResponse!: TenantDtoWebApiResponse;
            try{
                const tenantCommand: TenantUpdateCommand = {
                    tenant: selTenant
                };
                patchState(store, { isLoading: true });
                tenantResponse = await lastValueFrom(
                    tenantService.tenantPut(selTenant.id!, tenantCommand)
                );
                patchState(store, { isLoading: false });
            }catch (error) {
                console.error('Error in TenantStore.update(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
                //Create the Failed webApi Response return
                tenantResponse = {
                    isSuccess: false,
                    message: 'Tenant Updation Failed',
                    result: undefined,
                    errors: []
                }
            }
            return tenantResponse;
        },

        // Delete the Tenant
        delete: async (
            id?: string
        ): Promise<BooleanWebApiResponse> => {
            patchState(store, { isLoading: true });
            let tenantResponse!: BooleanWebApiResponse;
            try{
                tenantResponse = await lastValueFrom(
                    tenantService.tenantIdDelete(id!)
                );
                patchState(store, { isLoading: false });
            }catch (error) {
                console.error('Error in TenantStore.delete(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
                //Create the Failed webApi Response return
                tenantResponse = {
                    isSuccess: false,
                    message: 'Tenant Deletion Failed',
                    result: undefined,
                    errors: []
                }
            }
            return tenantResponse;
        },

        // Create a new subscription
        createSubscription: async (
            subscriptionCreateCommand: SubscriptionCreateCommand
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionCreatePut(
                        xTenantId,
                        subscriptionCreateCommand
                    )
                );
            }catch(error: any){
                console.error('Error in TenantStore.createSubscription(): ' + error);
                patchState(store, { isLoading: false });

                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Create Subscription Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Cancel the subscription
        cancelSubscription: async (
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionCancelPut(
                        xTenantId
                    )
                );
            }catch(error: any){
                console.error('Error in TenantStore.cancelSubscription(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Cancel Subscription Package Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Renewing the Subscription
        renewSubscription: async (
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionRenewPut(xTenantId)
                );
            }catch(error: any){
                console.error('Error in TenantStore.renewSubscription(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Renewal of Subscription Package Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Change the Subscription Package
        changeSubscriptionPackage: async (
            newPackageId?: string
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                const subcriptionPackageChangeCommand: SubcriptionPackageChangeCommand = {
                    newPackageId: newPackageId
                };
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionChangePackagePut(xTenantId, subcriptionPackageChangeCommand)
                );
            }catch(error: any){
                console.error('Error in TenantStore.changeSubscriptionPackage(): ' + error);
                patchState(store, { isLoading: false });

                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Change of Subscription Package Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Cancelling the Subscription Trail
        cancelSubscriptionTrail: async (): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionCancelTrailPut(xTenantId)
                );
            }catch(error: any){
                console.error('Error in TenantStore.cancelSubscriptionTrail(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Cancel Subscription Trail Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Activating the Subscription
        activateSubscription: async (
            tenandId?: string
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionActivatePut(tenandId!)
                );
            }catch(error: any){
                console.error('Error in TenantStore.activateSubscription(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Subscription Activation Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Deactivating the Subscription
        deactivateSubscription: async (
            tenandId?: string
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionDeactivatePut(tenandId!)
                );
            }catch(error: any){
                console.error('Error in TenantStore.deactivateSubscription(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Subscription Deactivation Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Extending the Subscription Trail
        extendSubscriptionTrail: async (
            tenandId?: string,
            extenstionPeriodInDays?: number
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const subscriptionExtendTrailCommand: SubscriptionExtendTrailCommand = {
                    extenstionPeriodInDays: extenstionPeriodInDays
                };
                response = await lastValueFrom(
                    tenantService.tenantSubscriptionExtendTrailPut(tenandId!, subscriptionExtendTrailCommand)
                );
            }catch(error: any){
                console.error('Error in TenantStore.extendSubscriptionTrail(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Extension of Subscription Trail Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        
        // Create a new Statutory
        createStatutory: async (
            newStatutoryInfo: StatutoryInfoCreateDto
        ): Promise<TenantDtoWebApiResponse> => {
            const statutoryCreateCommand: StatutoryCreateCommand = {
                statutory: newStatutoryInfo
            };
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantStatutoryCreatePut(
                        xTenantId,
                        statutoryCreateCommand
                    )
                );
            }catch(error: any){
                console.error('Error in TenantStore.createStatutory(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Create Statutory Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Update the Statutory
        updateStatutory: async (
            selStatutory: StatutoryInfoEditDto
        ): Promise<TenantDtoWebApiResponse> => {
            const statutoryUpdateCommand: StatutoryUpdateCommand = {
                statutory: selStatutory
            };
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                response = await lastValueFrom(
                    tenantService.tenantStatutoryUpdatePut(
                        xTenantId,
                        statutoryUpdateCommand
                    )
                );
            }catch(error: any){
                console.error('Error in TenantStore.updateStatutory(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Update Statutory Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Delete the Statutory
        deleteStatutory: async (
            id?: string
        ): Promise<BooleanWebApiResponse> => {
            patchState(store, { isLoading: true });
            let tenantResponse!: BooleanWebApiResponse;
            const xTenantId = storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
            try{
                tenantResponse = await lastValueFrom(
                    tenantService.tenantStatutoryDeleteIdPut(id!, xTenantId)
                );
                patchState(store, { isLoading: false });
            }catch(error: any){
                console.error('Error in TenantStore.deleteStatutory(): ' + error);
                patchState(store, {
                    isLoading: false,
                });
                //Create the Failed webApi Response return
                tenantResponse = {
                    isSuccess: false,
                    message: 'Delete Statutory Failed',
                    result: undefined,
                    errors: []
                }
            }
            return tenantResponse;
        },

        // Approve the Statutory
        approveStatutory: async (
            tenandId?: string,
            statutoryApprovedCommand?: StatutoryApprovedCommand
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                response = await lastValueFrom(
                    tenantService.tenantStatutoryApprovePut(tenandId!, statutoryApprovedCommand)
                );
            }catch(error: any){
                console.error('Error in TenantStore.approveStatutory(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Approve Statutory Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },

        // Reject the Statutory
        rejectStatutory: async (
            tenandId?: string,
            statutoryRejectCommand?: StatutoryRejectCommand
        ): Promise<TenantDtoWebApiResponse> => {
            let response!: TenantDtoWebApiResponse;
            try{
                patchState(store, { isLoading: true });
                response = await lastValueFrom(
                    tenantService.tenantStatutoryRejectPut(tenandId!, statutoryRejectCommand)
                );
            }catch(error: any){
                console.error('Error in TenantStore.rejectStatutory(): ' + error);
                patchState(store, { isLoading: false });
                //Create the Failed webApi Response return
                response = {
                    isSuccess: false,
                    message: 'Reject Statutory Failed',
                    result: undefined,
                    errors: []
                }
            }
            return response;
        },
    }))
);
